import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, Shield, Plus, X, Tag, HelpCircle, Sparkles, Bell, Zap, ArrowRight, Star, Loader } from 'lucide-react';
import { useMyContext } from '../../../../context/MyContext';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';
import { Base_Url, getWhatappMessages, saveWhatappMessages } from '../../../../config/config';

const CommunicationConfig = () => {
  const { isToggleSidebar, isDarkMode } = useMyContext();
  const [animatingAlert, setAnimatingAlert] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [alertsData, setAlertsData] = useState({});

  const vendor_Id = localStorage.getItem("cus_id");
  const API_BASE_URL = `${Base_Url}${getWhatappMessages}/${vendor_Id}`;

  // Alert mapping to display configuration
  const alertDisplayConfig = {
    'shipment_create': {
      displayName: 'Shipment Create',
      icon: Bell,
      color: 'emerald',
      recommended: true,
      features: ['Real-time notifications', 'Customer verification', 'Improved delivery success']
    },
    'shipment_ofd': {
      displayName: 'Shipment OFD',
      icon: MessageCircle,
      color: 'blue',
      recommended: true,
      features: ['Live tracking updates', 'Delivery time estimation', 'Customer preparation']
    },
    'shipment_delivered': {
      displayName: 'Shipment Delivered',
      icon: CheckCircle,
      color: 'purple',
      recommended: false,
      features: ['Delivery confirmation', 'Customer satisfaction', 'Order completion']
    },
    'testing': {
      displayName: 'Testing',
      icon: Shield,
      color: 'blue',
      isNew: true,
      features: ['Testing features', 'Quality assurance', 'Performance monitoring']
    }
  };

  // Fetch existing configuration on component mount
  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const cusId = localStorage.getItem('cus_id');
      const token = localStorage.getItem('token');

      const response = await fetch(API_BASE_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          cus_id: cusId,
          token
        }
      });

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        setVendorId(result.data.vendor_id);
        setAlertsData(result.data);
      } else {
        console.error('Failed to fetch configuration:', result.message);
        // Use mock data if API fails
        setAlertsData({
          "shipment_create": {
            "status": "inactive",
            "price": "6.00",
            "description": "tes"
          },
          "shipment_ofd": {
            "status": "inactive",
            "price": "2.90",
            "description": "This msg creates"
          },
          "shipment_delivered": {
            "status": "inactive",
            "price": "10.00",
            "description": "testing"
          },
          "testing": {
            "status": "inactive",
            "price": "50.00",
            "description": "testing"
          }
        });
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
      // Use mock data on error
      setAlertsData({
        "shipment_create": {
          "status": "inactive",
          "price": "6.00",
          "description": "tes"
        },
        "shipment_ofd": {
          "status": "inactive",
          "price": "2.90",
          "description": "This msg creates"
        },
        "shipment_delivered": {
          "status": "inactive",
          "price": "10.00",
          "description": "testing"
        },
        "testing": {
          "status": "inactive",
          "price": "50.00",
          "description": "testing"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    try {
      setSaving(true);
      const cusId = localStorage.getItem('cus_id') || sessionStorage.getItem('cus_id');
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      // Build message object with "1" for active and "0" for inactive
      const messagePayload = {};
      Object.keys(alertsData).forEach(key => {
        messagePayload[key] = alertsData[key].status === 'active' ? "1" : "0";
      });

      const payload = {
        vendor_id: vendorId || vendor_Id,
        message: messagePayload
      };

      console.log('Payload to send:', JSON.stringify(payload, null, 2));

      const response = await fetch(Base_Url + saveWhatappMessages, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'cus_id': cusId,
          token: token
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.status === 'success') {
        setShowSuccess('Configuration saved successfully!');
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        alert('Failed to save configuration. Please try again.');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Error saving configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAlert = (alertKey) => {
    setAnimatingAlert(alertKey);
    setTimeout(() => {
      setAlertsData(prev => ({
        ...prev,
        [alertKey]: {
          ...prev[alertKey],
          status: 'active'
        }
      }));
      setAnimatingAlert(null);
      setShowSuccess(`${alertDisplayConfig[alertKey]?.displayName || alertKey} added successfully!`);
      setTimeout(() => setShowSuccess(false), 2000);
    }, 300);
  };

  const handleRemoveAlert = (alertKey) => {
    setAlertsData(prev => ({
      ...prev,
      [alertKey]: {
        ...prev[alertKey],
        status: 'inactive'
      }
    }));
  };

  // Convert alertsData to alerts array
  const alerts = Object.keys(alertsData).map(key => {
    const alertData = alertsData[key];
    const displayConfig = alertDisplayConfig[key] || {};
    
    return {
      key: key,
      name: displayConfig.displayName || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      description: alertData.description || '',
      price: `₹${parseFloat(alertData.price || 0).toFixed(2)}`,
      status: alertData.status || 'inactive',
      isActive: alertData.status === 'active',
      icon: displayConfig.icon || Bell,
      color: displayConfig.color || 'blue',
      recommended: displayConfig.recommended || false,
      isNew: displayConfig.isNew || false,
      features: displayConfig.features || []
    };
  });

  const selectedAlertsData = alerts.filter(alert => alert.isActive);

  const calculateTotalCost = () => {
    return selectedAlertsData.reduce((total, alert) => {
      const priceValue = alert.price.replace('₹', '').trim();
      return total + parseFloat(priceValue);
    }, 0);
  };

  const getColorClasses = (color, isAdded) => {
    const colorMap = {
      emerald: {
        border: isAdded ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50' : 'border-gray-200 bg-white hover:border-emerald-200 hover:shadow-lg',
        icon: 'text-emerald-500',
        badge: 'bg-emerald-100 text-emerald-800',
        button: 'bg-emerald-500 hover:bg-emerald-600 text-white'
      },
      blue: {
        border: isAdded ? 'border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50' : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg',
        icon: 'text-blue-500',
        badge: 'bg-blue-100 text-blue-800',
        button: 'bg-blue-500 hover:bg-blue-600 text-white'
      },
      purple: {
        border: isAdded ? 'border-purple-300 bg-gradient-to-r from-purple-50 to-pink-50' : 'border-gray-200 bg-white hover:border-purple-200 hover:shadow-lg',
        icon: 'text-purple-500',
        badge: 'bg-purple-100 text-purple-800',
        button: 'bg-purple-500 hover:bg-purple-600 text-white'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  if (loading) {
    return (
      <div className={`h-screen flex items-center justify-center ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      {showSuccess && (
        <div className="fixed top-20 right-6 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 animate-bounce">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span>{showSuccess}</span>
          </div>
        </div>
      )}
      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )} 
        <div className={`flex-1 p-6 overflow-y-auto ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                    Communication by Pickupxpress
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4 text-green-500 animate-pulse" />
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </div>
              </div>
              <h1 className={`text-5xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Smart Communication
                </span>
              </h1>
              <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Enhance customer experience with intelligent notifications
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              {
                icon: MessageCircle,
                title: 'WhatsApp Integration',
                description: 'Send shipment updates for key milestones via WhatsApp',
                gradient: 'from-green-500 to-emerald-500',
                stats: '99.5% Delivery Rate'
              },
              {
                icon: CheckCircle,
                title: 'Real-time Verification',
                description: 'Verify non delivered orders to improve deliverability',
                gradient: 'from-blue-500 to-cyan-500',
                stats: '40% Better Success'
              },
              {
                icon: Shield,
                title: 'Brand Enhancement',
                description: 'Drive brand awareness through enhanced post-purchase experience',
                gradient: 'from-purple-500 to-pink-500',
                stats: '85% Customer Satisfaction'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className={`group ${isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20' : 'bg-white/80 backdrop-blur-xl border-gray-200'} p-6 rounded-2xl border hover:scale-105 transition-all duration-300 hover:shadow-2xl relative overflow-hidden`}
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-white/10 to-transparent rounded-full -mr-10 -mt-10"></div>
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className={`font-bold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm leading-relaxed mb-3`}>
                  {feature.description}
                </p>
                <div className={`text-xs font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-600'} flex items-center gap-1`}>
                  <Star className="w-3 h-3" />
                  {feature.stats}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className={`${isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20' : 'bg-white/90 backdrop-blur-xl border-gray-200'} rounded-2xl border p-8 shadow-2xl`}>
                <div className="flex items-center gap-4 mb-8">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Configure WhatsApp Alerts
                  </h2>
                </div>

                <div className="space-y-6">
                  <div className={`text-sm font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-600'} mb-6 flex items-center gap-2`}>
                    <Zap className="w-4 h-4" />
                    FORWARD SHIPMENTS
                  </div>

                  {alerts.map((alert, index) => {
                    const isAdded = alert.isActive;
                    const colorClasses = getColorClasses(alert.color, isAdded);
                    const IconComponent = alert.icon;

                    return (
                      <div key={index}>
                        <div className={`${isDarkMode ? 'bg-slate-800/50 backdrop-blur border-slate-700' : colorClasses.border} border rounded-2xl p-6 transition-all duration-300 ${animatingAlert === alert.key ? 'scale-105 shadow-xl' : ''} relative overflow-hidden`}>
                          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/5 to-transparent rounded-full -mr-12 -mt-12"></div>

                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-3">
                                <div className={`p-2 rounded-lg bg-gradient-to-r ${alert.color === 'emerald' ? 'from-emerald-500 to-green-500' : alert.color === 'blue' ? 'from-blue-500 to-cyan-500' : 'from-purple-500 to-pink-500'}`}>
                                  <IconComponent className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {alert.name}
                                  </h3>
                                  <div className="flex items-center gap-2 mt-1">
                                    {alert.recommended && (
                                      <span className="bg-gradient-to-r from-orange-400 to-orange-500 text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                                        <Star className="w-3 h-3" />
                                        Recommended
                                      </span>
                                    )}
                                    {alert.isNew && (
                                      <span className="bg-gradient-to-r from-green-400 to-green-500 text-white text-xs px-3 py-1 rounded-full font-medium animate-pulse">
                                        ✨ New
                                      </span>
                                    )}
                                    {isAdded && (
                                      <span className="bg-gradient-to-r from-green-400 to-green-500 text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />
                                        Active
                                      </span>
                                    )}
                                    {!isAdded && (
                                      <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full font-medium">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 leading-relaxed`}>
                                {alert.description}
                              </p>

                              <div className="flex flex-wrap gap-2 mb-4">
                                {alert.features?.map((feature, idx) => (
                                  <span key={idx} className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="flex items-center gap-4 ml-6">
                              <div className="text-right">
                                <span className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {alert.price}
                                </span>
                                <div className="text-xs text-gray-500">per message</div>
                              </div>
                              {isAdded ? (
                                <button
                                  onClick={() => handleRemoveAlert(alert.key)}
                                  className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-all hover:scale-110"
                                  title="Remove alert"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleAddAlert(alert.key)}
                                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl font-medium"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        {index < alerts.length - 1 && (
                          <div className="flex justify-center my-4">
                            <div className={`px-4 py-1 rounded-full text-sm font-medium ${isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-gray-100 text-gray-500'}`}>
                              OR
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className={`${isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20' : 'bg-white/90 backdrop-blur-xl border-gray-200'} rounded-2xl border p-6 sticky top-6 shadow-2xl`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg">
                    <Tag className="w-5 h-5 text-white" />
                  </div>
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Selected Alerts
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    {selectedAlertsData.length}
                  </span>
                </div>

                <div className="space-y-3 mb-6">
                  {selectedAlertsData.length === 0 ? (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <p className="text-sm">No alerts selected</p>
                      <p className="text-xs mt-2">Add alerts to get started</p>
                    </div>
                  ) : (
                    selectedAlertsData.map((alert, index) => {
                      const IconComponent = alert.icon;
                      return (
                        <div key={index} className={`flex justify-between items-center p-3 rounded-lg ${isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'} group hover:scale-105 transition-all`}>
                          <div className="flex items-center gap-3">
                            <IconComponent className="w-4 h-4 text-blue-500" />
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {alert.name}
                            </span>
                          </div>
                          <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {alert.price}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className={`border-t pt-6 mb-6 ${isDarkMode ? 'border-white/20' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Estimated cost per AWB
                    </span>
                    <span className={`font-bold text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      ₹ {calculateTotalCost().toFixed(2)}
                    </span>
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 flex items-start gap-2`}>
                    <span className="text-blue-500 mt-0.5">ℹ️</span>
                    <span>For NDR & Out for Delivery, a message will be delivered for every delivery attempt.</span>
                  </div>
                </div>

                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-6`}>
                  By activating Communication by Pickupxpress, you agree to our{' '}
                  <button className="text-blue-500 hover:underline font-medium">Terms and Conditions</button>
                </div>

                <button
                  onClick={saveConfiguration}
                  disabled={saving || selectedAlertsData.length === 0}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 px-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Activate Communication
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="fixed bottom-6 right-6">
            <button className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white p-4 rounded-2xl shadow-2xl flex items-center gap-2 hover:scale-110 transition-all duration-300 group">
              <HelpCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              <span className="text-sm font-medium hidden md:block">Help Center</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationConfig;